import { useMemo, useCallback } from 'react';
import axios from 'axios';
import { DASHBOARD_ENDPOINTS, STORAGE_KEYS } from '../constants/api';
import { useState, useEffect } from 'react';

export const useCompanyDashboard = (
    companyDocs: any[] | null,
    companyFieldValues: Record<string, any> | null,
    agencyId?: string
) => {
    const [ffcStatus, setFFCStatus] = useState<any>(null);
    const [beeStatus, setBEEStatus] = useState<any>(null);
    const [auditDeadline, setAuditDeadline] = useState<any>(null);
    const [infoOfficer, setInfoOfficer] = useState<{ registered: boolean; name: string } | null>(null);
    const now = useMemo(() => new Date(), []);

    const formatDate = (d: Date | string | null) => {
        if (!d) return '';
        const date = typeof d === 'string' ? new Date(d) : d;
        const newDate = date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
        return newDate;
    };

    const fetchFFCStatus = useCallback(async () => {
        if (!agencyId) {
            setFFCStatus(null);
            return;
        }
        try {
            const headers = {
                Authorization: `Bearer ${sessionStorage.getItem(STORAGE_KEYS.TOKEN)}`,
            };

            const response = await axios.get(DASHBOARD_ENDPOINTS.FFC_STATUS, {
                params: { agencyId },
                headers
            });
            const data = response.data;
            if (data && data.status && data.expiry) {
                const expiry = data.expiry ? new Date(data.expiry) : null;
                setFFCStatus({ status: data.status, found: true, expiry: expiry ? formatDate(expiry) : null, docId: data.docId || null });
                return;
            }
        } catch (error) {
            setFFCStatus({ status: "Failed", found: false, expiry: "N/A", docId: "N/A" });
            console.error('Error fetching FFC status:', error);
        }
    }, [agencyId]);

    const fetchComplianceStatus = useCallback(async () => {
        if (!agencyId) {
            setFFCStatus(null);
            return;
        }
        try {
            const headers = {
                Authorization: `Bearer ${sessionStorage.getItem(STORAGE_KEYS.TOKEN)}`,
            };

            const response = await axios.get(DASHBOARD_ENDPOINTS.BEE_STATUS, {
                params: { agencyId },
                headers
            });
            const data = response.data;
            if (data && data.status && data.expiry) {
                const expiry = data.expiry ? new Date(data.expiry) : null;
                setBEEStatus({ status: data.status, found: true, expiry: expiry ? formatDate(expiry) : null, docId: data.docId || null });
                return;
            }
        } catch (error) {
            setFFCStatus({ status: "Failed", found: false, expiry: "N/A", docId: "N/A" });
            console.error('Error fetching FFC status:', error);
        }
    }, [agencyId]);

    const fetchAuditDeadline = useCallback(async () => {
        const date = new Date();
        const financial_year_end = new Date(date.getFullYear(), 11, 31); // December 31st
        const days_remaining = Math.ceil((financial_year_end.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
        setAuditDeadline({ days_remaining: days_remaining, financial_year_end: financial_year_end });
    }, []);

    const fetchInformationOfficer = useCallback(async () => {
        setInfoOfficer(null);
    }, []);

    useEffect(() => {
        // Recompute local stats when inputs change
        fetchFFCStatus();
        fetchComplianceStatus();
        fetchAuditDeadline();
        fetchInformationOfficer();
    }, [companyDocs, companyFieldValues, agencyId, fetchFFCStatus, fetchComplianceStatus, fetchAuditDeadline, fetchInformationOfficer]);

    return { ffcStatus, beeStatus, auditDeadline, infoOfficer };
};